import React, { Component } from 'react'
import OktaAuth from '@okta/okta-auth-js'
import { withOktaAuth } from '@okta/okta-react'
import { Redirect } from 'react-router-dom'
import { ROUTES, LO_STO, SECONDARY_EMR_TYPE, EMR_INTEGRATION_STATUS } from '../lib/constants'
import { Loading } from '@sukiai/components'
import Login from './Login'
import FHIR from 'fhirclient'
import { OktaConfig } from '../config'
import loSto from '../config/loSto'
import { get, isNil } from 'lodash'
import jwtDecode from 'jwt-decode'
import { SearchAltaisUser, GetAmwellNote } from '@sukiai/gql/app'
import { logout, setError, startCreateNote } from '../lib/util'
import client from '../apollo'
import { GET_SOCKET_AUTHENTICATED, SET_CREATE_AMWELL_NOTE } from '../apollo/actions'
import { logger } from '../lib/logging'
import pubSub from '../lib/util/pubSub'
import { EMR_INTEGRATION_DETAILS_UPDATE } from '../constants/pubSub.events'
import { getEpicTokenRefreshHandler, getCernerTokenRefreshHandler } from '../helpers/emrIntegration'
import { updateEmrFhirTokens } from '../rest'
import { AMWELL_VIEWS } from '../constants/amwell'
import { EMBED_EMR } from '../constants/embed'

class Root extends Component {
  constructor (props) {
    super(props)

    this.state = {
      authenticated: null
    }

    const isAuthenticated = props.authState.isAuthenticated
    if (isAuthenticated !== null) {
      this.state = { authenticated: isAuthenticated }
    } else {
      this.state = { authenticated: null }
    }

    this.oktaAuth = new OktaAuth({
      url: OktaConfig.baseURL,
      issuer: OktaConfig.auth.issuer,
      clientId: OktaConfig.auth.client_id,
      redirectUri: OktaConfig.auth.redirect_uri,
      pkce: false,
      onAuthRequired: OktaConfig.auth.onAuthRequired
    })
  }

  componentDidUpdate () {
    this.checkAuthentication()
  }

  checkAuthentication = () => {
    const authenticated = this.props.authState.isAuthenticated
    if (authenticated !== this.state.authenticated) {
      this.setState({ authenticated })
    }
  }

  async componentDidMount () {
    const { history } = this.props

    const search = get(history, 'location.search', '')
    if (search.includes('cerner') || search.includes('code')) {
      this.setState({ isWaitingForFhirAuth: true })
      const emrIntegrationDetails = loSto.get(LO_STO.EMR_INTEGRATION_DETAILS) || {}
      if (emrIntegrationDetails.emr === SECONDARY_EMR_TYPE.EPIC_EMR) {
        const epicFhirConfig = emrIntegrationDetails.config
        var myHeaders = new Headers()
        myHeaders.append('Content-Type', 'application/x-www-form-urlencoded')
        myHeaders.append('Authorization', `Basic ${epicFhirConfig.clientSecret}`)

        const redirectUri = epicFhirConfig.redirectUri
        const clientId = epicFhirConfig.clientId
        const baseURL = epicFhirConfig.url
        const stateIndex = search.indexOf('&')
        const code = search.substring(6, stateIndex)

        var urlencoded = new URLSearchParams()
        urlencoded.append('code', code)
        urlencoded.append('grant_type', 'authorization_code')
        urlencoded.append('redirect_uri', redirectUri)
        urlencoded.append('client_id', clientId)

        var requestOptions = {
          method: 'POST',
          headers: myHeaders,
          body: urlencoded,
          redirect: 'follow'
        }

        // TODO: replace the following URL with SSO oauth url from the CONFIG file
        fetch(baseURL, requestOptions)
          .then(response => response.json())
          .then(respTxt => {
            // eslint-disable-next-line
            const accessToken = respTxt?.access_token
            // eslint-disable-next-line
            const idToken = respTxt?.id_token
            const refreshToken = respTxt?.refresh_token
            const expiryTime = respTxt?.expires_in

            loSto.set(LO_STO.FHIR_ACCESS_TOKEN, accessToken)
            loSto.set(LO_STO.FHIR_ID_TOKEN, idToken)
            loSto.set(LO_STO.EMR_INTEGRATION_DETAILS, {
              ...emrIntegrationDetails,
              status: EMR_INTEGRATION_STATUS.CONNECTED
            })
            // set epic token cache
            updateEmrFhirTokens(emrIntegrationDetails.emr, accessToken, idToken, refreshToken)
            this.setState({ authenticated: true, isWaitingForFhirAuth: false }, () => {
              pubSub.publish(EMR_INTEGRATION_DETAILS_UPDATE)
              const epicTokenRefreshHandler = getEpicTokenRefreshHandler(refreshToken, expiryTime)
              epicTokenRefreshHandler()
            })
          }).catch(error => {
            console.error('Unable to login to epic', error)
            this.setState({ authenticated: false, isWaitingForFhirAuth: false })
            history.push('/')
          })
      } else {
        FHIR.oauth2.ready()
          .then(client => {
            // eslint-disable-next-line
            const accessToken = client?.state?.tokenResponse?.access_token
            // eslint-disable-next-line
            const idToken = client?.state?.tokenResponse?.id_token
            const refreshToken = client?.state?.tokenResponse?.refresh_token
            const expiryTime = client?.state?.tokenResponse?.expires_in

            loSto.set(LO_STO.FHIR_ACCESS_TOKEN, accessToken)
            loSto.set(LO_STO.FHIR_ID_TOKEN, idToken)
            loSto.set(LO_STO.EMR_INTEGRATION_DETAILS, {
              ...emrIntegrationDetails,
              status: EMR_INTEGRATION_STATUS.CONNECTED
            })
            // set epic token cache
            updateEmrFhirTokens(emrIntegrationDetails.emr, accessToken, idToken, refreshToken)
            this.setState({ authenticated: true, isWaitingForFhirAuth: false }, () => {
              pubSub.publish(EMR_INTEGRATION_DETAILS_UPDATE)
              const cernerTokenRefreshHandler = getCernerTokenRefreshHandler(refreshToken, expiryTime)
              cernerTokenRefreshHandler()
            })
          }).catch((error) => {
            console.log('ERROR: ', error)
          })
      }
    }
  }

  componentDidUpdate = async (prevProps, prevState) => {
    if (this.props.authState.isAuthenticated && !this.state.authenticated) {
      this.setState({ authenticated: true })

      if (loSto.get(LO_STO.AMWELL_VIEW) === AMWELL_VIEWS.SCRIPTS) {
        this.props.history.push(`${ROUTES.SCRIPTS}/${EMBED_EMR.AMWELL}`)
        return
      }

      if (!loSto.get(LO_STO.AMWELL_APPOINTMENT_ID) && !loSto.get(LO_STO.IS_ALTAIS)) {
        return
      }

      const token = await this.props.authService.getIdToken()
      const jwtUser = jwtDecode(token)
      const userId = jwtUser?.userID
      const organizationId = jwtUser?.organizationID
      const athenaPatientId = loSto.get(LO_STO.ATHENA_PATIENT_ID)
      const athenaEncounterId = loSto.get(LO_STO.ATHENA_ENCOUNTER_ID)

      if (loSto.get(LO_STO.AMWELL_APPOINTMENT_ID)) {
        const appointmentIdFromLocalStorage = loSto.get(
          LO_STO.AMWELL_APPOINTMENT_ID
        )
        client
          .mutate({
            mutation: GetAmwellNote,
            variables: {
              organizationId,
              userId,
              appointmentId: appointmentIdFromLocalStorage
            }
          })
          .then(async ({ data }) => {
            if (data?.getAmwellNote) {
              const { compositionId, patientId, appointmentId } =
                data.getAmwellNote
              if (compositionId) {
                logger.info(
                  'Existing composition for this encounter/patient - naving to note'
                )
                this.props.history.push(`${ROUTES.NOTE}/${compositionId}`)
              } else {
                loSto.set(LO_STO.CURRENT_PATIENT_ID, patientId)
                loSto.set(LO_STO.CURRENT_APPT_ID, appointmentId)

                // If socket is not yet authenticated, need to queue up the create once AUTHENTICATED comes back
                const {
                  data: { socket }
                } = await client.query({
                  query: GET_SOCKET_AUTHENTICATED
                })

                if (socket.isAuthenticated) {
                  startCreateNote()
                } else {
                  client.mutate({
                    mutation: SET_CREATE_AMWELL_NOTE,
                    variables: { createNote: true }
                  })
                }
              }
            }
          })
          .catch((err) => {
            logger.error('Unable to GetAmwellNote', err)
          })
      }

      if (!loSto.get(LO_STO.IS_ALTAIS)) {
        return
      }

      if (!userId || !organizationId) {
        setError('Cannot log this user in. User is not registered in the system. Contact support@suki.ai for further assistance')
        this.setState({ authenticated: false })
        return
      }

      const searchAltaisVars = {
        userId,
        organizationId,
        email: jwtUser?.email,
        // eslint-disable-next-line
        username: jwtUser?.preferred_username
      }

      if (athenaPatientId) {
        searchAltaisVars.athenaPatientId = athenaPatientId
      }

      if (athenaEncounterId) {
        searchAltaisVars.athenaEncounterId = athenaEncounterId
      }

      const res = await client.query({
        query: SearchAltaisUser,
        variables: searchAltaisVars
      })

      if (!res) {
        console.error('This user does not exist in Suki’s system.')
        setError('This user does not exist in Suki’s system. Please contact support@suki.ai.')
        logout()
        return
      }

      if (!athenaPatientId && !athenaEncounterId) {
        this.props.history.push(`${ROUTES.SCHEDULE}`)
        return
      }

      const compositionId = res?.data?.searchAltaisUser?.compositionId
      const noteId = res?.data?.searchAltaisUser?.noteId
      const patientId = res?.data?.searchAltaisUser?.patient?.id
      const appt = res?.data?.searchAltaisUser?.appointment

      if (appt) {
        loSto.set(LO_STO.CURRENT_APPT_ID, appt.id)
      }

      if (patientId) {
        loSto.set(LO_STO.CURRENT_PATIENT_ID, patientId)
      }

      if (compositionId) {
        this.props.history.push(`${ROUTES.NOTE}/${compositionId}`)
        return
      }

      if (noteId) {
        this.props.history.push(`${ROUTES.PATIENT_NOTES}/${patientId}?note_id=${noteId}`)
        return
      }

      if (patientId && !appt) {
        // nav to patient view
        window.location.href = `${window.location.origin}${ROUTES.PATIENT_NOTES}/${patientId}`
        // this.props.history.push(`${ROUTES.PATIENT_NOTES}/${patientId}`)
        return
      }

      startCreateNote()
    }
  }

  render () {
    const { authenticated, isWaitingForFhirAuth } = this.state
    const altaisLoggedIn = loSto.get('okta-token-storage')?.idToken

    if (loSto.get(LO_STO.ALTAIS_ERROR)) {
      alert('This user does not exist in Suki’s system. Please contact support@suki.ai.')
      loSto.set(LO_STO.ALTAIS_ERROR, false)
    }

    if (authenticated === null || isWaitingForFhirAuth) {
      return (
        <div id='rootLoader' style={{ position: 'fixed', top: '50%', left: '50%' }}>
          <Loading />
        </div>
      )
    }

    if (altaisLoggedIn && !authenticated) {
      this.setState({ authenticated: true })
      console.log('altaisLoggedIn')
      location.reload()
      return <Redirect to={ROUTES.SCHEDULE} />
    }

    return authenticated
      ? <Redirect to={ROUTES.HOME} />
      : <Login />
  }
}

export default withOktaAuth(Root)
